// Recalcula a simulação inteira em memória a partir do dataset preparado + ajustes.
// Chamado a cada tecla: ~categorias x 12 multiplicações + somas por nó — sem rede.

import { MONTH_INDEXES, sumMonths, zeros12 } from './periods';
import { effectiveCategoryPct, isCategoryLinked, isOutsidePlanned, isValueMode } from './adjustments';
import { isZeroBaseMonth, projectValues } from './valueMode';
import {
  computeBaseIndicatorSeries,
  deriveIndicators,
  monthlyDerivedIndicators,
  sumBaseIndicators,
} from './indicators';

/** Valor lançado em R$ num mês sem base (orçamento base zero); null quando o mês segue o %. */
const zeroBaseValue = (base, values, m) => (isZeroBaseMonth(base, m) ? (values?.[m] || 0) : null);

/**
 * Receita Bruta base e projetada por mês, considerando só os ajustes das categorias de receita
 * (inclusive as receitas lançadas em R$ nos meses sem base). Soma em `into` quando informado.
 */
export const revenueTotals = (model, state, into = { base: zeros12(), projected: zeros12() }) => {
  model.revenueCats.forEach((cod) => {
    const base = model.base.get(cod) || zeros12();
    const values = state.values?.[cod];
    const pct = effectiveCategoryPct(model, state, cod);
    for (let m = 0; m < 12; m += 1) {
      into.base[m] += base[m];
      const typed = zeroBaseValue(base, values, m);
      into.projected[m] += typed ?? base[m] * (1 + (pct ? pct[m] : 0) / 100);
    }
  });
  return into;
};

/** r_m a partir dos totais de receita: projetado / base − 1 (0 sem base). */
export const variationOfTotals = ({ base, projected }) =>
  MONTH_INDEXES.map((m) => (base[m] ? projected[m] / base[m] - 1 : 0));

/** Variação da Receita Bruta por mês (r_m). */
export const revenueVariation = (model, state) => variationOfTotals(revenueTotals(model, state));

/**
 * Projetado por categoria, mês a mês:
 *  linha comum:              Base × (1 + a_m)
 *  vinculada à receita:      Base × (1 + r_m) × (1 + a_m)
 *  mês sem base:             o valor digitado em R$ (orçamento base zero; sem valor, 0)
 * `options.revenueVariation`: r_m de fora (no cenário por departamento, a receita da empresa).
 */
export const projectCategories = (model, state, options = {}) => {
  // Começar zerado: a projeção é o valor digitado (R$), sem % e sem "acompanha a receita".
  if (isValueMode(state)) return { projected: projectValues(model, state), revenueVariation: zeros12() };
  const r = options.revenueVariation || revenueVariation(model, state);
  const projected = new Map();
  model.catList.forEach((cod) => {
    const base = model.base.get(cod) || zeros12();
    const values = state.values?.[cod];
    const pct = effectiveCategoryPct(model, state, cod);
    const linked = isCategoryLinked(model, state, cod);
    const out = zeros12();
    for (let m = 0; m < 12; m += 1) {
      const typed = zeroBaseValue(base, values, m);
      if (typed !== null) {
        out[m] = typed;
        continue;
      }
      const own = 1 + (pct ? pct[m] : 0) / 100;
      out[m] = base[m] * own * (linked ? 1 + r[m] : 1);
    }
    projected.set(cod, out);
  });
  return { projected, revenueVariation: r };
};

/**
 * Valores por nó (soma da subárvore) e cascata dos totalizadores raiz — mesma regra da DRE
 * (applyCumulativeOnRootTotalizers): cada totalizador raiz = acumulado das raízes acima + o próprio.
 * Os nós da seção "Categorias fora da DRE" só somam as próprias categorias (fora da cascata).
 * Retorna Map(nodeId -> number[12]) com o valor EXIBIDO.
 */
export const computeStatement = (model, catValues) => {
  const own = new Map();
  [...model.orderedNodeIds, ...(model.outsideNodeIds || [])].forEach((id) => {
    const node = model.nodeById.get(id);
    const series = zeros12();
    node.allCats.forEach((cod) => {
      const values = catValues.get(cod);
      if (!values) return;
      for (let m = 0; m < 12; m += 1) series[m] += values[m];
    });
    own.set(id, series);
  });

  const display = new Map(own);
  const running = zeros12();
  model.rootIds.forEach((id) => {
    const node = model.nodeById.get(id);
    if (!node.isTotalizador) return;
    const series = own.get(id);
    for (let m = 0; m < 12; m += 1) running[m] += series[m];
    display.set(id, [...running]);
  });
  return display;
};

/**
 * Linha de resultado (gráfico e rodapé do ranking): Lucro Líquido configurado nos indicadores;
 * sem configuração, o último totalizador raiz do DRE. Sem totalizador raiz (cenário por
 * departamento), o último totalizador da tabela — o resultado (entradas − saídas).
 */
export const resultSeries = (model, statement, baseIndicators) => {
  if (model.indicatorCats.LL?.size) return { key: 'LL', label: 'Lucro Líquido', series: baseIndicators.LL };
  const lastTotalOf = (ids) => [...ids].reverse().find((id) => model.nodeById.get(id).isTotalizador);
  const lastTotal = lastTotalOf(model.rootIds) || lastTotalOf(model.orderedNodeIds);
  if (lastTotal) {
    return { key: lastTotal, label: model.nodeById.get(lastTotal).descricao, series: statement.get(lastTotal) };
  }
  return { key: null, label: 'Resultado', series: zeros12() };
};

const variationPct = (proj, base) => (base ? ((proj - base) / Math.abs(base)) * 100 : null);

/** Linhas do ranking "O que mais mexeu no resultado": nós de 1º nível editáveis. */
export const rankingLines = (model) => {
  const ids = [];
  model.rootIds.forEach((rootId) => {
    const root = model.nodeById.get(rootId);
    if (root.isTotalizador) root.childIds.forEach((childId) => ids.push(childId));
    else ids.push(rootId);
  });
  return ids;
};

/** Simulação completa. Base é calculada uma vez (memoizada por modelo pelo chamador). */
export const computeBaseSnapshot = (model) => {
  const statement = computeStatement(model, model.base);
  const baseIndicatorSeries = computeBaseIndicatorSeries(model, model.base);
  return {
    catValues: model.base,
    statement,
    baseIndicatorSeries,
    indicatorsMonthly: monthlyDerivedIndicators(baseIndicatorSeries),
    indicatorsYear: deriveIndicators(sumBaseIndicators(baseIndicatorSeries)),
    result: resultSeries(model, statement, baseIndicatorSeries),
  };
};

export const simulate = (model, state, baseSnapshot = computeBaseSnapshot(model), options = {}) => {
  const { projected, revenueVariation: r } = projectCategories(model, state, options);
  return finishSimulation(model, projected, r, baseSnapshot);
};

/**
 * Resto da simulação a partir do projetado por categoria: DRE, indicadores, KPIs e ranking. No cenário
 * por departamento, roda sobre a soma dos departamentos (o consolidado).
 */
export const finishSimulation = (model, projected, r, baseSnapshot) => {
  const statement = computeStatement(model, projected);
  const baseIndicatorSeries = computeBaseIndicatorSeries(model, projected);
  const indicatorsMonthly = monthlyDerivedIndicators(baseIndicatorSeries);
  const indicatorsYear = deriveIndicators(sumBaseIndicators(baseIndicatorSeries));
  const result = resultSeries(model, statement, baseIndicatorSeries);

  const kpiKeys = ['RL', 'LB', 'EBITDA', 'LL'];
  const kpis = kpiKeys.map((key) => {
    const proj = indicatorsYear[key];
    const base = baseSnapshot.indicatorsYear[key];
    return {
      key,
      projected: proj,
      base,
      variationPct: variationPct(proj, base),
      configured: (model.indicatorCats[key]?.size || 0) > 0,
    };
  });
  const mlProj = indicatorsYear.MARGEM_LIQUIDA_PCT;
  const mlBase = baseSnapshot.indicatorsYear.MARGEM_LIQUIDA_PCT;
  kpis.push({
    key: 'MARGEM_LIQUIDA_PCT',
    projected: mlProj,
    base: mlBase,
    variationPp: mlProj - mlBase,
    configured: (model.indicatorCats.LL?.size || 0) > 0 && (model.indicatorCats.RL?.size || 0) > 0,
  });

  const ranking = rankingLines(model)
    .map((id) => {
      const proj = sumMonths(statement.get(id));
      const base = sumMonths(baseSnapshot.statement.get(id));
      return { nodeId: id, label: model.nodeById.get(id).descricao, delta: proj - base };
    })
    .filter((x) => Math.abs(x.delta) >= 0.5)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const resultDelta = sumMonths(result.series) - sumMonths(baseSnapshot.result.series);

  return {
    catValues: projected,
    statement,
    revenueVariation: r,
    baseIndicatorSeries,
    indicatorsMonthly,
    indicatorsYear,
    result,
    kpis,
    ranking,
    resultDelta,
  };
};

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Projetado por categoria x mês para a API: é o orçamento que o Gestão e o Studio comparam com o
 * realizado. Categorias com valor lançado que não estão no DRE atual (ex.: migradas do orçamento do
 * Sync) seguem com o valor lançado, para salvar o cenário não apagar esse orçamento.
 * Com o `model`, as categorias fora da DRE só entram quando planejadas no cenário (isOutsidePlanned).
 */
export const projectedValuesToApi = (simulation, state, model = null) => {
  const out = [];
  simulation.catValues.forEach((series, cod) => {
    if (model?.outsideCatSet?.has(cod) && !isOutsidePlanned(model, state, cod)) return;
    const monthlyValues = MONTH_INDEXES.map((m) => round2(series[m]));
    if (monthlyValues.some((v) => v)) out.push({ codCategoria: cod, monthlyValues });
  });
  Object.entries(state.values || {}).forEach(([cod, values]) => {
    if (simulation.catValues.has(cod)) return;
    const monthlyValues = MONTH_INDEXES.map((m) => round2(values[m]));
    if (monthlyValues.some((v) => v)) out.push({ codCategoria: cod, monthlyValues });
  });
  return out;
};
